import { useState, useEffect, useCallback } from 'react';
import ConfigurationService from '../services/ConfigurationService';
import { CONFIG_EVENTS } from '../constants';
import type { LabelParams } from '../types/configuration.types';

/**
 * useLabels Hook
 * 
 * Provides high-performance access to dynamic UI labels.
 * Subscribes to configuration updates to ensure labels are reactive across the entire application.
 * 
 * @performance 
 * - Leverages ConfigurationService O(1) Map-based label registry.
 * - Minimal re-render overhead via local state tick.
 * - Label retrieval is memoized at the service level.
 * 
 * @security Sanitized output via ConfigurationService.
 * @returns An object containing the getLabel function.
 */
export const useLabels = () => {
    // Local state to trigger re-render on global config updates
    const [, setTick] = useState(0);

    useEffect(() => {
        /**
         * Listener for global configuration updates (e.g., from WebSockets or Manual Reload).
         * Ensures that UI text updates immediately without requiring a full page refresh.
         */
        const handleUpdate = () => {
            setTick(t => t + 1);
        };

        window.addEventListener(CONFIG_EVENTS.UPDATED, handleUpdate);
        return () => window.removeEventListener(CONFIG_EVENTS.UPDATED, handleUpdate);
    }, []);

    /**
     * Retrieves a dynamic label with optional interpolation variables.
     * Use this instead of hardcoded strings to fulfill the 100% Dynamic UI mandate.
     */
    const getLabel = useCallback((key: string, defaultValueOrParams?: string | LabelParams, params?: LabelParams): string => {
        return ConfigurationService.getLabel(key, defaultValueOrParams, params);
    }, []);

    return { getLabel };
};
